/** @jsxImportSource @emotion/react */
import React, { useMemo, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { css } from "@emotion/react";
import { Flex } from "@phoenix/components";
import { ProjectMetricViewProps } from "@phoenix/pages/project/metrics/types";
import { downloadCSV } from "@phoenix/utils/csvUtils";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";

const tableContainerCSS = css`
  background: var(--ac-global-background-color-white);
  border: 1px solid var(--ac-global-color-grey-200);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
`;

const tableCSS = css`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  table-layout: fixed;
`;

const thCSS = css`
  position: sticky;
  top: 0;
  background: var(--ac-global-color-grey-50);
  z-index: 1;
  padding: 12px 0;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--ac-global-text-color-400);
  border-bottom: 1px solid var(--ac-global-color-grey-100);
`;

const tdCSS = css`
  padding: 14px 0;
  font-size: 13px;
  color: var(--ac-global-text-color-700);
  border-bottom: 1px solid var(--ac-global-color-grey-50);
`;

const rowCSS = css`
  &:hover {
    background: var(--ac-global-color-grey-50);
  }
`;

const selectCSS = css`
  background: var(--ac-global-color-grey-100);
  border: 1px solid var(--ac-global-color-grey-300);
  border-radius: 8px;
  color: var(--ac-global-text-color-900);
  font-size: 12px;
  padding: 6px 12px;
  cursor: pointer;
  outline: none;
  min-width: 150px;
  &:hover {
    border-color: var(--ac-global-color-primary);
  }
  & option {
    background: var(--ac-global-background-color-white, #ffffff);
    color: #1f2937; /* Force dark text color so option text is visible on browser white background */
  }
`;

const scoreColor = (score: number, name: string) => {
  return "#4f46e5";
};

const scoreBadgeCSS = (score: number, name: string) => {
  const color = scoreColor(score, name);
  return css`
    color: ${color};
    font-weight: 600;
    font-size: 12px;
    background: ${color}10;
    padding: 4px 10px;
    border-radius: 6px;
    display: inline-block;
    border: 1px solid ${color}20;
  `;
};

export const getScorerColor = (name: string) => {
    const colors = ["#06b6d4", "#8b5cf6", "#f59e0b", "#10b981", "#3b82f6", "#ec4899", "#f43f5e", "#14b8a6"];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash % colors.length);
    return colors[index];
};

export const squareIconCSS = (name: string) => {
    const color = getScorerColor(name);
    return css`
        width: 10px;
        height: 10px;
        border-radius: 2px;
        background: ${color};
        display: inline-block;
        margin-right: 12px;
    `;
};

export function QualitySummaryTable({
  projectId,
  timeRange,
  fetchKey,
  onDataExport,
}: ProjectMetricViewProps) {
  const [selectedScorer, setSelectedScorer] = useState("all");
  const [selectedAgent, setSelectedAgent] = useState("all");
  const [selectedKind, setSelectedKind] = useState("all");

  const data = useLazyLoadQuery<any>(
    graphql`
      query QualitySummaryTableQuery($id: ID!, $timeRange: TimeRange!) {
        project: node(id: $id) {
          ... on Project {
            spanAnnotationAgentSummaries(timeRange: $timeRange) {
              name
              agentName
              meanScore
              annotatorKind
              numScores
            }
            spanAnnotationScoreTimeSeries(timeRange: $timeRange) {
              data {
                timestamp
                scoresWithLabels {
                  label
                  score
                  agentName
                  annotatorKind
                }
              }
            }
          }
        }
      }
    `,
    { 
      id: projectId,
      timeRange: {
        start: timeRange.start?.toISOString(),
        end: timeRange.end?.toISOString(),
      }
    },
    { fetchKey, fetchPolicy: "network-only" }
  );

  const summaries = useMemo(() => {
    return data.project?.spanAnnotationAgentSummaries ?? [];
  }, [data]);

  const scorers = useMemo(() => {
    return Array.from(new Set(summaries.map((s: any) => s.name)));
  }, [summaries]);

  const agents = useMemo(() => {
    return Array.from(new Set(summaries.map((s: any) => s.agentName).filter(Boolean)));
  }, [summaries]);

  const chartData = useMemo(() => {
    const raw = data.project?.spanAnnotationScoreTimeSeries?.data ?? [];
    
    // Group and average scores by day (YYYY-MM-DD) to prevent duplicate date tick labels
    const dailyGroups: { [dateStr: string]: { [label: string]: number[] } } = {};
    
    raw.forEach((d: any) => {
      if (!d.timestamp) return;
      const dateKey = new Date(d.timestamp).toISOString().split('T')[0];
      if (!dailyGroups[dateKey]) {
        dailyGroups[dateKey] = {};
      }
      
      (d.scoresWithLabels ?? []).forEach((item: any) => {
        // Filter by Agent Name
        if (selectedAgent !== "all" && item.agentName !== selectedAgent) {
          return;
        }
        // Filter by Annotator Kind
        if (selectedKind !== "all" && item.annotatorKind !== selectedKind) {
          return;
        }

        if (!dailyGroups[dateKey][item.label]) {
          dailyGroups[dateKey][item.label] = [];
        }
        dailyGroups[dateKey][item.label].push(item.score);
      });
    });
    
    // Only include dates that have actual filtered scores to zoom onto the active evaluation range
    const activeDateKeys = Object.keys(dailyGroups)
      .filter((dateKey) => {
        const groups = dailyGroups[dateKey];
        return Object.values(groups).some((scores) => scores && scores.length > 0);
      })
      .sort();
    
    let chartStart: Date;
    let chartEnd: Date;
    
    if (activeDateKeys.length === 0) {
      chartStart = timeRange.start ? new Date(timeRange.start) : new Date(Date.now() - 24 * 60 * 60 * 1000);
      chartEnd = timeRange.end ? new Date(timeRange.end) : new Date();
    } else {
      const firstDataDate = new Date(activeDateKeys[0]);
      const lastDataDate = new Date(activeDateKeys[activeDateKeys.length - 1]);
      
      // Pad 3 days before/after, but keep within timeRange boundaries (zoom in)
      const paddedStart = new Date(firstDataDate.getTime() - 3 * 24 * 60 * 60 * 1000);
      chartStart = timeRange.start && paddedStart > new Date(timeRange.start) 
        ? paddedStart 
        : (timeRange.start ? new Date(timeRange.start) : paddedStart);
      
      const paddedEnd = new Date(lastDataDate.getTime() + 3 * 24 * 60 * 60 * 1000);
      chartEnd = timeRange.end && paddedEnd < new Date(timeRange.end) 
        ? paddedEnd 
        : (timeRange.end ? new Date(timeRange.end) : paddedEnd);
      
      if (chartEnd < lastDataDate) {
        chartEnd = lastDataDate;
      }
    }
    
    const chartPoints: any[] = [];
    const current = new Date(Date.UTC(chartStart.getUTCFullYear(), chartStart.getUTCMonth(), chartStart.getUTCDate()));
    const stop = new Date(Date.UTC(chartEnd.getUTCFullYear(), chartEnd.getUTCMonth(), chartEnd.getUTCDate()));
    
    while (current <= stop) {
      const dateKey = current.toISOString().split('T')[0];
      const timestamp = current.toISOString();
      const point: any = { timestamp };
      
      // Default all active scorers to 0 so we have continuous data points for smooth curves
      scorers.forEach((name) => {
        point[name] = 0;
      });
      
      if (dailyGroups[dateKey]) {
        Object.keys(dailyGroups[dateKey]).forEach((label) => {
          const scores = dailyGroups[dateKey][label];
          if (scores && scores.length > 0) {
            const avg = scores.reduce((sum, s) => sum + s, 0) / scores.length;
            point[label] = avg;
          }
        });
      }
      
      chartPoints.push(point);
      current.setUTCDate(current.getUTCDate() + 1);
    }
    
    return chartPoints;
  }, [data, selectedAgent, selectedKind, timeRange]);

  const handleDownloadCSV = () => {
      const exportData = summaries.map((s: any) => ({
          "Scorer": s.name.replace(/_/g, " "),
          "Agent Name": s.agentName || "Default Agent",
          "Average Value": s.meanScore != null ? `${s.meanScore.toFixed(2)}%` : "--"
      }));
      downloadCSV(exportData, "quality_summary.csv");
  };

  const prevExportDataRef = React.useRef<string>("");

  React.useEffect(() => {
      if (onDataExport && summaries.length > 0) {
          const exportData = summaries.map((s: any) => ({
              "Scorer": s.name.replace(/_/g, " "),
              "Agent Name": s.agentName || "Default Agent",
              "Average Value": s.meanScore != null ? `${s.meanScore.toFixed(2)}%` : "--"
          }));
          const currentDataStr = JSON.stringify(exportData);
          if (prevExportDataRef.current !== currentDataStr) {
              prevExportDataRef.current = currentDataStr;
              onDataExport("quality_summary", "Quality Summary", exportData);
          }
      }
  }, [summaries, onDataExport]);

  const timeTickFormatter = (value: string) => {
    const date = new Date(value);
    return date.toLocaleString('en-US', { month: 'numeric', day: 'numeric' });
  };

  return (
    <div css={tableContainerCSS}>
      <div css={css`display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; border-bottom: 1px solid var(--ac-global-color-grey-100); background: var(--ac-global-color-grey-50);`}>
        <div css={css`font-weight: 700; font-size: 15px; color: var(--ac-global-text-color-900);`}>
            ✨ Quality Summary
        </div>
        <button 
            css={css`
                background: var(--ac-global-background-color-white);
                border: 1px solid var(--ac-global-color-grey-200);
                border-radius: 6px;
                padding: 6px 12px;
                font-size: 12px;
                font-weight: 600;
                color: var(--ac-global-text-color-600);
                cursor: pointer;
                &:hover { background: var(--ac-global-color-grey-100); }
            `}
            onClick={handleDownloadCSV}
        >
            📥 Download CSV
        </button>
      </div>
      <div style={{ padding: '24px 40px', background: 'var(--ac-global-color-grey-50)' }}>
        <Flex direction="row" gap="size-300" wrap="wrap" alignItems="start">
          {/* Left panel: Table */}
          <div style={{ flex: '1 1 500px', minWidth: '350px', maxHeight: '540px', overflowY: 'auto' }}>
            <table css={tableCSS}>
              <thead>
                <tr>
                  <th css={thCSS} style={{ width: '220px', paddingLeft: '28px' }}>Scorer</th>
                  <th css={thCSS} style={{ width: '180px' }}>Agent Name</th>
                  <th css={thCSS} style={{ paddingLeft: '40px' }}>Average Value</th>
                </tr>
              </thead>
              <tbody>
                {summaries.map((summary: any) => (
                  <tr key={`${summary.name}-${summary.agentName}-${summary.annotatorKind}`} css={rowCSS}>
                    <td css={tdCSS} style={{ width: '220px', paddingLeft: '28px' }}>
                      <Flex direction="column" gap="size-25">
                        <Flex alignItems="center" gap="size-50">
                          <div css={squareIconCSS(summary.name)} />
                          <span style={{ fontWeight: 500 }}>{summary.name.replace(/_/g, " ")}</span>
                          <span style={{
                            fontSize: '9px',
                            fontWeight: '700',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            textTransform: 'uppercase',
                            background: summary.annotatorKind === 'LLM' ? '#ecfeff' : summary.annotatorKind === 'CODE' ? '#fff7ed' : '#f0f9ff',
                            color: summary.annotatorKind === 'LLM' ? '#0891b2' : summary.annotatorKind === 'CODE' ? '#ea580c' : '#0284c7',
                            border: `1px solid ${summary.annotatorKind === 'LLM' ? '#cffafe' : summary.annotatorKind === 'CODE' ? '#ffedd5' : '#e0f2fe'}`
                          }}>
                            {summary.annotatorKind}
                          </span>
                        </Flex>
                        <span style={{
                          fontSize: '11px',
                          color: 'var(--ac-global-text-color-400)',
                          paddingLeft: '22px'
                        }}>
                          {summary.numScores} {summary.numScores === 1 ? 'score' : 'scores'} recorded
                        </span>
                      </Flex>
                    </td>
                    <td css={tdCSS} style={{ width: '180px' }}>
                      <span style={{ fontWeight: 400 }}>{summary.agentName}</span>
                    </td>
                    <td css={tdCSS} style={{ paddingLeft: '40px' }}>
                      {summary.meanScore != null ? (
                        <span css={scoreBadgeCSS(summary.meanScore, summary.name)}>
                          {summary.meanScore.toFixed(2)}%
                        </span>
                      ) : (
                        <span style={{ color: "var(--ac-global-text-color-400)" }}>--</span>
                      )}
                    </td>
                  </tr>
                ))}
                {summaries.length === 0 && (
                  <tr>
                    <td colSpan={3} css={tdCSS} style={{ textAlign: 'center', padding: '40px', color: 'var(--ac-global-text-color-400)' }}>
                      No quality metrics found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Right panel: Area Chart (Model usage style) */}
          {summaries.length > 0 && (
            <div style={{
              flex: '1 1 400px',
              minWidth: '320px',
              background: 'var(--ac-global-background-color-white)',
              borderRadius: '12px',
              border: '1px solid var(--ac-global-color-grey-200)',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--ac-global-text-color-800)' }}>
                    📈 Quality Trends
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {/* Scorer Filter */}
                  <select 
                    css={selectCSS} 
                    value={selectedScorer} 
                    onChange={(e) => setSelectedScorer(e.target.value)}
                    style={{ flex: '1 1 110px', fontSize: '11px', padding: '4px 8px', minWidth: '110px' }}
                  >
                    <option value="all">All Scorers</option>
                    {scorers.map(name => (
                      <option key={name} value={name}>{name.replace(/_/g, " ")}</option>
                    ))}
                  </select>

                  {/* Agent Filter */}
                  <select 
                    css={selectCSS} 
                    value={selectedAgent} 
                    onChange={(e) => setSelectedAgent(e.target.value)}
                    style={{ flex: '1 1 110px', fontSize: '11px', padding: '4px 8px', minWidth: '110px' }}
                  >
                    <option value="all">All Agents</option>
                    {agents.map(name => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>

                  {/* Annotator Kind Filter */}
                  <select 
                    css={selectCSS} 
                    value={selectedKind} 
                    onChange={(e) => setSelectedKind(e.target.value)}
                    style={{ flex: '1 1 110px', fontSize: '11px', padding: '4px 8px', minWidth: '110px' }}
                  >
                    <option value="all">All Sources</option>
                    <option value="LLM">LLM Only</option>
                    <option value="CODE">CODE Only</option>
                    <option value="HUMAN">HUMAN Only</option>
                  </select>
                </div>
              </div>

              <div style={{ width: '100%', height: '260px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      {scorers.map((name) => {
                        const color = getScorerColor(name);
                        return (
                          <linearGradient key={`grad-${name}`} id={`grad-${name}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={color} stopOpacity={0.4} />
                            <stop offset="95%" stopColor={color} stopOpacity={0.05} />
                          </linearGradient>
                        );
                      })}
                    </defs>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" opacity={0.3} />
                    <XAxis
                      dataKey="timestamp"
                      interval="preserveStartEnd"
                      minTickGap={80}
                      tickFormatter={timeTickFormatter}
                      tick={{ fontSize: 10, fill: 'var(--chart-axis-label-color)' }}
                    />
                    <YAxis
                      domain={[0, 100]}
                      tick={{ fontSize: 10, fill: 'var(--chart-axis-label-color)' }}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <Tooltip
                      formatter={(value) => [`${Number(value).toFixed(2)}%`, "Average"]}
                      labelFormatter={timeTickFormatter}
                      contentStyle={{ 
                        borderRadius: '8px', 
                        border: '1px solid var(--ac-global-color-grey-200)', 
                        background: 'var(--ac-global-background-color-white)',
                        color: 'var(--ac-global-text-color-900)',
                        fontSize: '12px' 
                      }}
                    />
                    {selectedScorer === "all" ? (
                      scorers.map((name) => (
                        <Area
                          key={name}
                          type="monotone"
                          dataKey={name}
                          name={name.replace(/_/g, " ")}
                          stroke={getScorerColor(name)}
                          strokeWidth={2.5}
                          fill={`url(#grad-${name})`}
                          connectNulls={true}
                          dot={{ r: 2 }}
                          activeDot={{ r: 4 }}
                        />
                      ))
                    ) : (
                      <Area
                        type="monotone"
                        dataKey={selectedScorer}
                        name={selectedScorer.replace(/_/g, " ")}
                        stroke={getScorerColor(selectedScorer)}
                        strokeWidth={3}
                        fill={`url(#grad-${selectedScorer})`}
                        connectNulls={true}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                    )}
                    <Legend 
                      iconType="circle" 
                      wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }} 
                      formatter={(value) => <span style={{ color: 'var(--ac-global-text-color-700)' }}>{value}</span>}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </Flex>
      </div>
    </div>
  );
}